import { useEffect } from 'react'
import { Capacitor } from '@capacitor/core'
import { GenericOAuth2 } from '@capacitor-community/generic-oauth2'
import Config from '../services/config'

interface Auth0NativeProps {
  isVisible: boolean
  onAuthSuccess: (tokens: any) => void
  onAuthError: (error: string) => void
  onClose: () => void
}

export const Auth0Native: React.FC<Auth0NativeProps> = ({
  isVisible,
  onAuthSuccess,
  onAuthError,
  onClose
}) => {
  useEffect(() => {
    if (!isVisible || !Capacitor.isNativePlatform()) return

    const authenticate = async () => {
      try {
        console.log('📱 Starting native Auth0 authentication')
        console.log('📱 Config values:', {
          domain: Config.AUTH0_DOMAIN,
          clientId: Config.AUTH0_CLIENT_ID,
          audience: Config.AUTH0_AUDIENCE,
          org: Config.AUTH0_ORG
        })

        const options = {
          appId: Config.AUTH0_CLIENT_ID,
          authorizationBaseUrl: `https://${Config.AUTH0_DOMAIN}/authorize`,
          accessTokenEndpoint: `https://${Config.AUTH0_DOMAIN}/oauth/token`,
          scope: 'openid profile email read:devices read:device_data read:locations read:events read:alarms',
          responseType: 'code',
          redirectUrl: 'com.silverfallscapital.omniiqmobile://callback',
          pkceEnabled: true, // Enable PKCE for secure flow
          additionalParameters: {
            audience: Config.AUTH0_AUDIENCE,
            organization: Config.AUTH0_ORG,
            prompt: 'login', // Force Auth0 to show login screen every time
            max_age: '0' // Don't use cached authentication
          },
          ios: {
            // Use ASWebAuthenticationSession on iOS
            customHandlerClass: undefined,
            presentationContextProvider: undefined
          },
          android: {
            // Android specific options if needed
            responseType: 'code',
            handleResultOnNewIntent: true,
            handleResultOnActivityResult: true
          },
          web: {
            // Web fallback
            windowOptions: 'width=400,height=600',
            windowTarget: '_blank'
          }
        }

        console.log('📱 OAuth2 options:', options)

        // Perform the OAuth2 authentication
        const result = await GenericOAuth2.authenticate(options)

        console.log('📱 Native Auth0 success:', result)

        // The plugin returns tokens in access_token_response
        const tokenResponse = result.access_token_response || result

        if (tokenResponse.access_token) {
          const tokens = {
            access_token: tokenResponse.access_token,
            id_token: tokenResponse.id_token,
            scope: tokenResponse.scope || 'openid profile email read:devices read:device_data read:locations read:events read:alarms',
            expires_in: tokenResponse.expires_in || 86400,
            token_type: tokenResponse.token_type || 'Bearer',
            refresh_token: tokenResponse.refresh_token
          }

          console.log('📱 Parsed tokens successfully, calling onAuthSuccess')
          onAuthSuccess(tokens)
        } else {
          onAuthError('No access token received')
        }

        onClose()

      } catch (error) {
        console.error('📱 Native Auth0 error:', error)
        console.error('📱 Error details:', {
          message: error instanceof Error ? error.message : 'Unknown error',
          error: error,
          stack: error instanceof Error ? error.stack : undefined
        })

        // Check if user cancelled
        if (error instanceof Error && error.message.includes('USER_CANCELLED')) {
          console.log('📱 User cancelled authentication')
        }

        onAuthError(error instanceof Error ? error.message : 'Authentication failed')
        onClose()
      }
    }

    // Add a small delay to ensure the component is properly mounted
    const timer = setTimeout(() => {
      authenticate()
    }, 100)

    return () => clearTimeout(timer)
  }, [isVisible, onAuthSuccess, onAuthError, onClose])

  // No UI needed - the native plugin handles the presentation
  return null
}